<template>
  <v-btn color="success" :loading="exporting" @click="exportTemplate">
    <v-icon left>mdi-file-excel</v-icon>
    Export Invoice Template
  </v-btn>
</template>

<script>
// npm install xlsx-js-style
import XLSXStyle from 'xlsx-js-style'

export default {
  name: 'ExportInvoiceTemplate',

  props: {
    // Array of objects — add your own data here later
    data: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return { exporting: false }
  },

  methods: {
    exportTemplate() {
      this.exporting = true
      try {
        const wb = XLSXStyle.utils.book_new()
        wb.Props = { SheetNames: [] }

        XLSXStyle.utils.book_append_sheet(wb, this.buildSerialNumberSheet(), 'Serial Number')
        XLSXStyle.utils.book_append_sheet(wb, this.buildMainSheet(),         'SLPH_Invoice_Cash_Debit_Credit')

        const out = XLSXStyle.write(wb, { bookType: 'xlsx', type: 'array' })
        this.download(out, 'Import_Sales_Invoice.xlsx')
      } finally {
        this.exporting = false
      }
    },

    // ─── helpers ────────────────────────────────────────────────────────────
    c(ws, addr, value, style) {
      const isEmpty = value === null || value === undefined
      const t = (!isEmpty && typeof value === 'number') ? 'n' : 's'
      ws[addr] = { v: isEmpty ? '' : value, t, s: style }
    },

    thin() {
      const s = { style: 'thin', color: { rgb: '000000' } }
      return { top: s, bottom: s, left: s, right: s }
    },

    fill(rgb) { return { patternType: 'solid', fgColor: { rgb } } },

    font(bold = false) { return { name: 'Calibri', sz: 11, bold } },

    hdr(fillRgb) {
      return {
        font:      this.font(true),
        fill:      fillRgb ? this.fill(fillRgb) : undefined,
        alignment: { horizontal: 'center', vertical: 'center' },
        border:    this.thin(),
      }
    },

    data_s(halign = 'general') {
      return {
        font:      this.font(false),
        alignment: { horizontal: halign, vertical: 'bottom' },
        border:    this.thin(),
      }
    },

    // ─── Serial Number sheet ────────────────────────────────────────────────
    buildSerialNumberSheet() {
      const ws = {}
      const C  = (a, v, s) => this.c(ws, a, v, s)
      const GREEN  = 'C5E0B4'
      const YELLOW = 'FFFF00'
      const WHITE  = 'FFFFFF'

      // Row 1 – color key (B1, C1 are italic like main sheet)
      C('A1', 'Color Key', { font: { name: 'Calibri', sz: 11, bold: true,  italic: false }, border: this.thin(), alignment: { vertical: 'top' } })
      C('B1', 'Note',      { font: { name: 'Calibri', sz: 11, bold: false, italic: true  }, fill: this.fill(GREEN),  border: this.thin(), alignment: { vertical: 'bottom' } })
      C('C1', 'Required',  { font: { name: 'Calibri', sz: 11, bold: false, italic: true  }, fill: this.fill(YELLOW), border: this.thin(), alignment: { vertical: 'center' } })

      // Row 3 – note (green, B3 has text, A3 & C3 empty green)
      C('A3', '', { font: this.font(), fill: this.fill(GREEN), alignment: { vertical: 'center' } })
      C('B3', 'The stock item sequence at detail grid.\n\nStart from 1', {
        font:      this.font(),
        fill:      this.fill(GREEN),
        alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
      })
      C('C3', '', { font: this.font(), fill: this.fill(GREEN), alignment: { vertical: 'center' } })

      // Row 5 – headers (all yellow, bold, centered)
      C('A5', 'DOCNO(20)',      this.hdr(YELLOW))
      C('B5', '_SEQ',           this.hdr(YELLOW))
      C('C5', '__SERIALNUMBER', this.hdr(YELLOW))

      // Row 6 – sample data row (white fill)
      const sampleStyle = { font: this.font(), fill: this.fill(WHITE), alignment: { horizontal: 'left', vertical: 'top' }, border: this.thin() }
      C('A6', '',  sampleStyle)
      C('B6', 1,   sampleStyle)
      C('C6', '',  sampleStyle)

      ws['!ref']  = 'A1:C6'
      ws['!cols'] = [{ wch: 13.29 }, { wch: 23.57 }, { wch: 17 }]
      ws['!rows'] = [
        { hpt: 15    }, // 1
        {},             // 2
        { hpt: 54.75 }, // 3
        {},             // 4
        { hpt: 16.5  }, // 5
        { hpt: 16.5  }, // 6
      ]
      return ws
    },

    // ─── Main invoice sheet ─────────────────────────────────────────────────
    buildMainSheet() {
      const ws = {}
      const C  = (a, v, s) => this.c(ws, a, v, s)

      const GREEN  = 'C5E0B4'
      const YELLOW = 'FFFF00'
      const PINK   = 'EAD1DC'   // e-invoice only
      const ORANGE = 'F9CB9C'   // 3rd party
      const BLUE   = 'BDD7EE'   // additional note
      const WHITE  = 'FFFFFF'
      const AMBER  = 'F4B183'   // from-doc

      // All 59 columns A→BG
      const allCols = [
        'A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T',
        'U','V','W','X','Y','Z','AA','AB','AC','AD','AE','AF','AG','AH','AI','AJ','AK',
        'AL','AM','AN','AO','AP','AQ','AR','AS','AT','AU','AV','AW','AX','AY','AZ','BA',
        'BB','BC','BD','BE','BF','BG',
      ]

      // ── ROW 1: Color Key ──────────────────────────────────────────────────
      C('A1', 'Color Key',          { font: { name: 'Calibri', sz: 11, bold: true,  italic: false }, border: this.thin(), alignment: { vertical: 'top'    } })
      C('B1', 'Note',               { font: { name: 'Calibri', sz: 11, bold: false, italic: true  }, fill: this.fill(GREEN),  border: this.thin(), alignment: { vertical: 'bottom' } })
      C('C1', 'Required',           { font: { name: 'Calibri', sz: 11, bold: false, italic: true  }, fill: this.fill(YELLOW), border: this.thin(), alignment: { vertical: 'center' } })
      C('D1', 'Only For E-Invoice', { font: { name: 'Calibri', sz: 11, bold: false, italic: true  }, fill: this.fill(PINK),   border: this.thin(), alignment: { vertical: 'top'    } })
      C('E1', 'Only for 3rd party', { font: { name: 'Calibri', sz: 11, bold: false, italic: true  }, fill: this.fill(ORANGE), border: this.thin(), alignment: { vertical: 'bottom', wrapText: true } })
      C('F1', 'Additional Note',    { font: { name: 'Calibri', sz: 11, bold: false, italic: true  }, fill: this.fill(BLUE),   border: this.thin(), alignment: { vertical: 'bottom' } })

      // ── ROW 3: Notes (green across all columns) ───────────────────────────
      allCols.forEach(col =>
        C(`${col}3`, null, { font: this.font(), fill: this.fill(GREEN), alignment: { vertical: 'center', wrapText: true } })
      )
      // Cells with text
      const notes = {
        C3:  { v: 'DD/MM/YYYY' },
        D3:  { v: 'Customer/Supplier Code' },
        E3:  { v: '* E-Invoicing Submitted Date time in the UTC timezone\n* eg 19/07/2024 7:17:43 AM' },
        F3:  { v: '* E-Invoicing Submited respond UUID\n*eg FSEP22A4Y5BFGGWAX95N943811' },
        G3:  { v: '* E-Invoicing Submited respond Long UUID\n* eg TM9AFNQ0M76T07BTX95NX43J10CzvSE31712365482' },
        T3:  { v: 'Credit Terms Code' },
        AE3: { v: '0 - Empty\n1 - Reg No(New)\n2 - NRIC\n3 - Passport\n4 - ARMY\n5 - Reg No(Old)' },
        AH3: { v: 'For Supplier only\n\n* Malaysia Standard Industrial Classification (MSIC) Codes\n* https://sdk.myinvois.hasil.gov.my/codes/#msic-codes', hyperlink: true },
        AI3: { v: '* A set of international trade rules that define the responsibilities of buyers and suppliers.The input of special characters is not allowed.' },
        AJ3: { v: '0 = None\n17 = E-Invoice / Self Billing(for Purchase)\n18 = Consolidate' },
        AK3: { v: 'GL Sales/Purchase Account Code' },
        AL3: { v: 'Product/SKU Code' },
        AM3: { v: 'Product/SKU Description' },
        AU3: { v: 'Leave empty to calculate automatically' },
        AV3: { v: 'F = False\nT = True' },
        AW3: { v: 'Leave empty to calculate automatically' },
        AX3: { v: '* Category of products or services being billed as a result of a commercial transaction. More than 1 classification codes can be added for goods / services included in the e-Invoice.\n* https://sdk.myinvois.hasil.gov.my/codes/classification-codes/', hyperlink: true },
        AY3: { v: 'For Tax Exemption Code use only' },
        BE3: { v: '[Sales]\nQT = Quotation\nSO = Sales Order\nDO = Delivery Order\n IV = Invoice (For CN only)\nDN = Debit Note For CN only)\nCS = Cash Sales For CN only)\n\n[Purchase]\nPQ = Purchase Request\nPO = Purchase Order\nGR = Goods Received\nPI = Purchase Invoice (For CN only)\nCP = Cash Purchase (For CN only)\nSD = Purchase Debit Note (For CN only)' },
        BG3: { v: 'Start From 1' },
      }
      Object.entries(notes).forEach(([addr, obj]) => {
        const isLink = obj.hyperlink === true
        C(addr, obj.v, {
          font: {
            name: 'Calibri', sz: 11,
            color:     { rgb: isLink ? '0000FF' : '000000' },
            underline: isLink ? 'single' : false,
            italic:    false,
          },
          fill:      this.fill(GREEN),
          alignment: { vertical: 'center', wrapText: true },
        })
      })

      // ── ROW 4: Additional note bar (blue) — merge A4:H4 so text shows fully ─
      const row4Style = { font: this.font(false), fill: this.fill(BLUE), alignment: { vertical: 'bottom' } }
      C('A4', '1. Item Code is necessary when stock items are available', row4Style)
      ;['B','C','D','E','F','G','H'].forEach(col => C(`${col}4`, '', row4Style))
      // Merge A4:H4 so text is fully visible
      if (!ws['!merges']) ws['!merges'] = []
      ws['!merges'].push({ s: { r: 3, c: 0 }, e: { r: 3, c: 7 } })

      // ── ROW 6: Headers ────────────────────────────────────────────────────
      const headers = [
        // [col, label, fillRgb]
        ['A',  'DOCNO(20)',                 YELLOW],
        ['B',  'DOCNOEX',                   WHITE ],
        ['C',  'DOCDATE',                   YELLOW],
        ['D',  'CODE(10)',                  YELLOW],
        ['E',  'EIV_UTC',                   ORANGE],
        ['F',  'IRBM_UUID',                 ORANGE],
        ['G',  'IRBM_LONGID',               ORANGE],
        ['H',  'IRBM_STATUS',               ORANGE],
        ['I',  'COMPANYNAME(100)',           PINK  ],
        ['J',  'ADDRESS1(60)',               PINK  ],
        ['K',  'ADDRESS2(60)',               PINK  ],
        ['L',  'ADDRESS3(60)',               PINK  ],
        ['M',  'ADDRESS4(60)',               PINK  ],
        ['N',  'POSTCODE(10)',               PINK  ],
        ['O',  'CITY(50)',                   PINK  ],
        ['P',  'STATE(50)',                  PINK  ],
        ['Q',  'COUNTRY(2)',                 PINK  ],
        ['R',  'PHONE1(200)',                PINK  ],
        ['S',  'AGENT(10)',                  null  ],
        ['T',  'TERMS(10)',                  null  ],
        ['U',  'DESCRIPTION(200)',           WHITE ],
        ['V',  'PROJECT(20)',                null  ],
        ['W',  'CC(200)',                    null  ],
        ['X',  'DOCREF1',                    null  ],
        ['Y',  'DOCREF2',                    null  ],
        ['Z',  'DOCREF3',                    null  ],
        ['AA', 'DOCREF4',                    null  ],
        ['AB', 'SALESTAXNO(25)',             PINK  ],
        ['AC', 'SERVICETAXNO(25)',           PINK  ],
        ['AD', 'TIN(14)',                    PINK  ],
        ['AE', 'IDTYPE',                     PINK  ],
        ['AF', 'IDNO(20)',                   PINK  ],
        ['AG', 'TOURISMNO(17)',              PINK  ],
        ['AH', 'SIC(10)',                    PINK  ],
        ['AI', 'INCOTERMS(3)',               PINK  ],
        ['AJ', 'SUBMISSIONTYPE',             PINK  ],
        ['AK', '_ACCOUNT(10)',               YELLOW],
        ['AL', '_ITEMCODE(30)',              BLUE  ],
        ['AM', '_DESCRIPTION(200)',          YELLOW],
        ['AN', '_DESCRIPTION2',             null  ],
        ['AO', '_DESCRIPTION3',             null  ],
        ['AP', '_QTY',                       YELLOW],
        ['AQ', '_UOM(10)',                   YELLOW],
        ['AR', '_UNITPRICE',                 YELLOW],
        ['AS', '_DISC(20)',                  null  ],
        ['AT', '_TAX(10)',                   null  ],
        ['AU', '_TAXAMT',                    WHITE ],
        ['AV', '_TAXINCLUSIVE',             null  ],
        ['AW', '_AMOUNT',                    null  ],
        ['AX', '_IRBM_CLASSIFICATION(3)',   PINK  ],
        ['AY', '_TAXEXEMPTIONREASON(300)',  PINK  ],
        ['AZ', '_LOCATION(20)',             null  ],
        ['BA', '_BATCH(30)',                 null  ],
        ['BB', '_PROJECT(20)',               null  ],
        ['BC', '_REMARK1(200)',              null  ],
        ['BD', '_REMARK2(200)',              null  ],
        ['BE', '_FROMDOCTYPE',               AMBER ],
        ['BF', '_FROMDOCNO',                 AMBER ],
        ['BG', '_FROMSEQNO',                 AMBER ],
      ]
      headers.forEach(([col, label, fillRgb]) => {
        C(`${col}6`, label, this.hdr(fillRgb))
      })

      // ── ROWS 7+: Data rows ─────────────────────────────────────────────────
      const rows = this.data.length ? this.data : []
      rows.forEach((item, idx) => {
        const r = idx + 7
        const d = (v, h = 'general') => ({ ...this.data_s(h), font: this.font() })

        C(`A${r}`,  item.order_number     || '<<New>>',   this.data_s())
        C(`B${r}`,  '',                                   this.data_s())
        C(`C${r}`,  item.formatted_date   || '',          this.data_s())
        C(`D${r}`,  item.SQL_export_code  || '',          this.data_s())
        C(`E${r}`,  '',  this.data_s()) // EIV_UTC
        C(`F${r}`,  '',  this.data_s()) // IRBM_UUID
        C(`G${r}`,  '',  this.data_s()) // IRBM_LONGID
        C(`H${r}`,  '',  this.data_s()) // IRBM_STATUS
        C(`I${r}`,  '',  this.data_s()) // COMPANYNAME
        C(`J${r}`,  '',  this.data_s()) // ADDRESS1
        C(`K${r}`,  '',  this.data_s()) // ADDRESS2
        C(`L${r}`,  '',  this.data_s()) // ADDRESS3
        C(`M${r}`,  '',  this.data_s()) // ADDRESS4
        C(`N${r}`,  '',  this.data_s()) // POSTCODE
        C(`O${r}`,  '',  this.data_s()) // CITY
        C(`P${r}`,  '',  this.data_s()) // STATE
        C(`Q${r}`,  '',  this.data_s()) // COUNTRY
        C(`R${r}`,  '',  this.data_s()) // PHONE1
        C(`S${r}`,  '----', this.data_s())
        C(`T${r}`,  '30 Days', this.data_s('left'))
        C(`U${r}`,  item.description || 'Purchase', this.data_s('left'))
        C(`V${r}`,  '----', this.data_s('left'))
        C(`W${r}`,  '',  this.data_s()) // CC
        C(`X${r}`,  '',  this.data_s()) // DOCREF1
        C(`Y${r}`,  '',  this.data_s()) // DOCREF2
        C(`Z${r}`,  '',  this.data_s()) // DOCREF3
        C(`AA${r}`, '',  this.data_s()) // DOCREF4
        C(`AB${r}`, '',  this.data_s()) // SALESTAXNO
        C(`AC${r}`, '',  this.data_s()) // SERVICETAXNO
        C(`AD${r}`, item.tin || '', this.data_s())
        C(`AE${r}`, item.idtype !== undefined ? item.idtype : 1, this.data_s())
        C(`AF${r}`, item.idno || '', this.data_s())
        C(`AG${r}`, '',  this.data_s()) // TOURISMNO
        C(`AH${r}`, '',  this.data_s()) // SIC
        C(`AI${r}`, '',  this.data_s()) // INCOTERMS
        C(`AJ${r}`, item.submissiontype !== undefined ? item.submissiontype : 17, this.data_s())
        C(`AK${r}`, item.account || '510-000', this.data_s('left'))
        C(`AL${r}`, item.itemcode || '', this.data_s())
        C(`AM${r}`, item.item_description || '', this.data_s())
        C(`AN${r}`, '',  this.data_s()) // DESC2
        C(`AO${r}`, '',  this.data_s()) // DESC3
        C(`AP${r}`, item.qty !== undefined ? item.qty : 1, this.data_s())
        C(`AQ${r}`, item.uom || 'UNIT', this.data_s())
        C(`AR${r}`, item.unit_price !== undefined ? item.unit_price : 0, this.data_s())
        C(`AS${r}`, '',  this.data_s()) // DISC
        C(`AT${r}`, '',  this.data_s()) // TAX
        C(`AU${r}`, '',  { ...this.data_s(), fill: this.fill(WHITE) }) // TAXAMT auto
        C(`AV${r}`, 'F', this.data_s()) // TAXINCLUSIVE
        C(`AW${r}`, '',  this.data_s()) // AMOUNT auto
        C(`AX${r}`, '',  this.data_s('left')) // IRBM_CLASS
        C(`AY${r}`, '',  this.data_s('left')) // TAXEXEMPTION
        C(`AZ${r}`, '----', this.data_s('left'))
        C(`BA${r}`, '',  this.data_s('left'))
        C(`BB${r}`, '',  this.data_s('left'))
        C(`BC${r}`, '',  this.data_s('left'))
        C(`BD${r}`, '',  this.data_s('left'))
        C(`BE${r}`, item.fromdoctype || 'GR', this.data_s())
        C(`BF${r}`, item.fromdocno  || '',    this.data_s())
        C(`BG${r}`, idx + 1, this.data_s())
      })

      const lastRow = Math.max((rows.length || 0) + 6, 6)
      ws['!ref'] = `A1:BG${lastRow}`

      // ── Column widths ─────────────────────────────────────────────────────
      ws['!cols'] = [
        { wch: 11.43 }, // A
        { wch: 14.29 }, // B
        { wch: 13.43 }, // C
        { wch: 22.00 }, // D
        { wch: 16.57 }, // E
        { wch: 15.29 }, // F
        { wch: 15.14 }, // G
        { wch: 18.71 }, // H
        { wch: 20.71 }, // I
        { wch: 15.71 }, // J
        { wch: 14.29 }, // K
        { wch: 14.14 }, // L
        { wch: 14.43 }, // M
        { wch: 14.71 }, // N
        { wch: 10.43 }, // O
        { wch:  9.43 }, // P
        { wch: 14.14 }, // Q
        { wch: 12.86 }, // R
        { wch: 10.43 }, // S
        { wch: 16.71 }, // T
        { wch: 25.29 }, // U
        { wch: 15.29 }, // V
        { wch: 30.14 }, // W
        { wch: 16.00 }, // X
        { wch: 18.14 }, // Y
        { wch: 14.00 }, // Z
        { wch: 14.43 }, // AA
        { wch: 16.00 }, // AB
        { wch: 22.29 }, // AC
        { wch: 29.00 }, // AD
        { wch: 31.43 }, // AE
        { wch: 31.57 }, // AF
        { wch: 15.86 }, // AG
        { wch: 18.71 }, // AH
        { wch: 28.14 }, // AI
        { wch: 29.57 }, // AJ
        { wch: 14.43 }, // AK
        { wch: 14.86 }, // AL
        { wch: 18.43 }, // AM
        { wch: 14.43 }, // AN
        { wch: 14.43 }, // AO
        { wch:  8.86 }, // AP
        { wch: 14.86 }, // AQ
        { wch: 16.57 }, // AR
        { wch: 25.14 }, // AS
        { wch: 28.29 }, // AT
        { wch: 25.29 }, // AU
        { wch: 25.29 }, // AV
        { wch: 25.14 }, // AW
        { wch: 25.14 }, // AX
        { wch: 28.29 }, // AY
        { wch: 25.29 }, // AZ
        { wch: 14.43 }, // BA
        { wch: 13.14 }, // BB
        { wch: 15.14 }, // BC
        { wch: 15.14 }, // BD
        { wch: 38.29 }, // BE
        { wch: 17.57 }, // BF
        { wch: 14.43 }, // BG
      ]

      // ── Row heights ───────────────────────────────────────────────────────
      const rowHeights = [
        { hpt: 15     }, // 1
        {},              // 2
        { hpt: 243.75 }, // 3 (extra tall to fit all multi-line notes without cutoff)
        { hpt: 15     }, // 4
        { hpt: 15     }, // 5
        { hpt: 16.5   }, // 6 header
      ]
      rows.forEach(() => rowHeights.push({ hpt: 14.25 }))
      ws['!rows'] = rowHeights

      return ws
    },

    download(buffer, filename) {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    },
  },
}
</script>